import { IReactionDisposer, makeObservable, observable, action, computed, reaction, runInAction } from 'mobx';
import axios, { CancelTokenSource } from 'axios';
import { uniqBy } from 'lodash';
import { NodeBaseDto, ActionParams, ContractComparisonParameters } from '../../types';
import { InputBaseModel, NodeBaseModel, ComparisonNodeBaseModel } from '..';
import { InstanceUtils } from '../../misc';
import { NodeFactory, ComparisonNodeFactory } from '../../factories';
import { TypeUtils } from '../../../common/misc';
import { Utils } from '../../../common/misc/Utils';
import { SourceStore } from '../../../custom_shared/stores';
import { NodeIds, InputIds } from '../../misc';
import ContractComparisonStore from '../../stores/ContractComparisonStore';

const tooltipTitles = {
    failedToFetch: 'Failed to fetch previous contract',
    missingBusinessId: 'Enter Business ID manually to search.'
} as const;

const summaryPreparationTimeoutMs = 15_000;
const summaryRegenerationDelayMs = 300;
const summaryRefreshPollIntervalMs = 100;

type CompareActionState = 'missingBusinessId' | 'readyToPrepare' | 'preparing' | 'readyToCompare' | 'notFound';

type FetchPreviousContractStatus = 'idle' | 'failed' | 'notFound';

type CompareActionIcon = 'loading' | 'check' | null;

type CompareActionViewModel = {
    state: CompareActionState;
    label: string;
    tooltipTitle: string | null;
    isClickable: boolean;
    icon: CompareActionIcon;
};

type SummaryPayloadState = {
    requestJson: string;
    signatureHash: number;
};

export default class ContractComparisonModel {
    newContractNodes: NodeBaseModel[] = [];

    previousContractNodes: NodeBaseModel[] = [];

    comparisonNodes: ComparisonNodeBaseModel[] = [];

    generatedSummary: string | null = null;

    fetchPreviousContractStatus: FetchPreviousContractStatus = 'idle';

    isLoading: boolean = false;

    isSelected: boolean = false;

    isExecutingAction: boolean = false;

    isSummaryLoading: boolean = false;

    private fetchDataCancelSource: CancelTokenSource | null = null;

    private summaryCancelSource: CancelTokenSource | null = null;

    private lastRequestedSummaryPayloadHash: number | null = null;

    private lastCompletedSummaryPayloadHash: number | null = null;

    private lastGeneratedSummaryPayloadHash: number | null = null;

    private summaryRefreshPromise: Promise<void> | null = null;

    private summaryRefreshPayloadHash: number | null = null;

    private readonly summaryRegenerationDisposer: IReactionDisposer;

    constructor(
        private readonly store: ContractComparisonStore,
        private readonly sourceStore: SourceStore,
        private readonly businessIdInput: InputBaseModel,
        private readonly parameters: ContractComparisonParameters
    ) {
        makeObservable<
            ContractComparisonModel,
            | 'lastRequestedSummaryPayloadHash'
            | 'lastCompletedSummaryPayloadHash'
            | 'lastGeneratedSummaryPayloadHash'
            | 'summaryPayloadState'
        >(this, {
            newContractNodes: observable,
            previousContractNodes: observable,
            comparisonNodes: observable,
            generatedSummary: observable,
            isLoading: observable,
            fetchPreviousContractStatus: observable,
            isSelected: observable,
            isExecutingAction: observable,
            isSummaryLoading: observable,
            lastRequestedSummaryPayloadHash: observable,
            lastCompletedSummaryPayloadHash: observable,
            lastGeneratedSummaryPayloadHash: observable,
            setIsSelected: action,
            reset: action,
            businessId: computed,
            hasPreviousContractNodes: computed,
            newContractInputs: computed,
            newContractInputsWithSource: computed,
            previousContractInputs: computed,
            previousContractInputsWithSource: computed,
            newContractBusinessTypeInput: computed,
            previousContractBusinessTypeInput: computed,
            isAnyInputLoading: computed,
            hasFreshSummary: computed,
            hasResolvedSummaryForCurrentPayload: computed,
            summaryPayloadHash: computed,
            summaryPayloadState: computed,
            businessTypeConflict: computed,
            compareAction: computed
        });

        this.summaryRegenerationDisposer = reaction(
            () => ({
                hasResolvedSummaryForCurrentPayload: this.hasResolvedSummaryForCurrentPayload,
                isAnyInputLoading: this.isAnyInputLoading,
                isComparisonSummaryConfigured: this.store.isComparisonSummaryConfigured,
                isLoading: this.isLoading,
                isSelected: this.isSelected,
                summaryPayloadHash: this.summaryPayloadHash
            }),
            summaryState => {
                if (
                    !summaryState.isSelected ||
                    summaryState.isLoading ||
                    summaryState.isAnyInputLoading ||
                    !summaryState.isComparisonSummaryConfigured ||
                    summaryState.summaryPayloadHash === null ||
                    summaryState.hasResolvedSummaryForCurrentPayload
                ) {
                    return;
                }

                this.generateContractComparisonSummary();
            },
            {
                delay: summaryRegenerationDelayMs
            }
        );
    }

    private static getBusinessType(nodes: NodeBaseModel[]): InputBaseModel | undefined {
        const generalInformationNode = nodes.find(node => node.id === NodeIds.GeneralInformation);

        if (!InstanceUtils.isBasicNode(generalInformationNode)) {
            return undefined;
        }

        return generalInformationNode.getInputById(InputIds.GeneralInformation.BusinessType);
    }

    private static wait(timeoutMs: number) {
        return new Promise<void>(resolve => {
            setTimeout(resolve, timeoutMs);
        });
    }

    get businessId() {
        return TypeUtils.isString(this.businessIdInput.value) ? this.businessIdInput.value.trim() : null;
    }

    get hasPreviousContractNodes() {
        return this.previousContractNodes.length > 0;
    }

    get newContractInputs() {
        return this.newContractNodes.flatMap(node => node.inputs);
    }

    get newContractInputsWithSource() {
        return this.newContractInputs.filter(InstanceUtils.isInputWithSource);
    }

    get previousContractInputs() {
        return this.previousContractNodes.flatMap(node => node.inputs);
    }

    get previousContractInputsWithSource() {
        return this.previousContractInputs.filter(InstanceUtils.isInputWithSource);
    }

    get newContractBusinessTypeInput() {
        return ContractComparisonModel.getBusinessType(this.newContractNodes);
    }

    get previousContractBusinessTypeInput() {
        return ContractComparisonModel.getBusinessType(this.previousContractNodes);
    }

    get isAnyInputLoading(): boolean {
        return (
            this.newContractInputsWithSource.some(input => input.isLoading) ||
            this.previousContractInputsWithSource.some(input => input.isLoading)
        );
    }

    get businessTypeConflict(): string | null {
        const newContractBusinessTypeInput = this.newContractBusinessTypeInput;
        const previousContractBusinessTypeInput = this.previousContractBusinessTypeInput;

        if (
            !InstanceUtils.isInputWithSource(newContractBusinessTypeInput) ||
            !InstanceUtils.isInputWithSource(previousContractBusinessTypeInput)
        ) {
            return null;
        }

        if (newContractBusinessTypeInput.value === previousContractBusinessTypeInput.value) {
            return null;
        }

        const newTypeLabel = newContractBusinessTypeInput.formattedValue;
        const previousTypeLabel = previousContractBusinessTypeInput.formattedValue;

        return `Business type mismatch. You can't save changes because the previous type (${previousTypeLabel}) doesn't match the current type (${newTypeLabel}).`;
    }

    setIsSelected(isSelected: boolean) {
        this.isSelected = isSelected;
    }

    get hasFreshSummary(): boolean {
        return (
            !!this.generatedSummary &&
            this.summaryPayloadHash !== null &&
            this.lastGeneratedSummaryPayloadHash === this.summaryPayloadHash
        );
    }

    get hasResolvedSummaryForCurrentPayload(): boolean {
        return this.summaryPayloadHash !== null && this.lastCompletedSummaryPayloadHash === this.summaryPayloadHash;
    }

    get summaryPayloadHash(): number | null {
        return this.summaryPayloadState?.signatureHash ?? null;
    }

    get compareAction(): CompareActionViewModel {
        if (!this.businessId) {
            return {
                state: 'missingBusinessId',
                label: 'Compare with previous period?',
                tooltipTitle: tooltipTitles.missingBusinessId,
                isClickable: false,
                icon: null
            };
        }

        if (this.isLoading) {
            return {
                state: 'preparing',
                label: 'Preparing previous contract...',
                tooltipTitle: null,
                isClickable: false,
                icon: 'loading'
            };
        }

        if (this.hasPreviousContractNodes) {
            return {
                state: 'readyToCompare',
                label: 'Previous contract ready to compare',
                tooltipTitle: null,
                isClickable: true,
                icon: 'check'
            };
        }

        if (this.fetchPreviousContractStatus === 'notFound') {
            return {
                state: 'notFound',
                label: 'Previous contract not found',
                tooltipTitle: null,
                isClickable: false,
                icon: null
            };
        }

        return {
            state: 'readyToPrepare',
            label: 'Compare with previous period',
            tooltipTitle: this.fetchPreviousContractStatus === 'failed' ? tooltipTitles.failedToFetch : null,
            isClickable: true,
            icon: null
        };
    }

    async executeContractComparisonAction(actionId: string, actionParams: ActionParams = {}) {
        try {
            runInAction(() => {
                this.isExecutingAction = true;
            });

            const response = await this.store.executeContractComparisonAction(this.newContractNodes, actionId, {
                ...actionParams,
                LayerId: this.parameters.layerId
            });

            if (!response.isOk()) {
                return;
            }

            response.map(nodes =>
                runInAction(() => {
                    try {
                        this.newContractNodes = NodeFactory.createOrUpdateNodes(
                            nodes,
                            this.newContractNodes,
                            this.sourceStore.sources
                        );
                    } catch (error) {
                        console.error('Error while creating or updating new contract nodes: ', error);
                    }

                    this.newContractInputsWithSource
                        .filter(input => !input.useCachedSourceOptions)
                        .forEach(input => input.fetchSourceOptions());

                    this.refreshSummaryState();
                })
            );
        } catch (error) {
            console.error('Error while executing contract comparison action: ', error);
        } finally {
            runInAction(() => {
                this.isExecutingAction = false;
            });
        }
    }

    async generateContractComparisonSummary() {
        if (!this.store.isComparisonSummaryConfigured) {
            return;
        }

        const targetPayloadHash = this.summaryPayloadHash;

        if (
            targetPayloadHash === null ||
            this.hasFreshSummary ||
            this.lastCompletedSummaryPayloadHash === targetPayloadHash
        ) {
            return;
        }

        try {
            const summaryRefreshPromise = this.ensureSummaryRefreshPromise(targetPayloadHash);
            await summaryRefreshPromise;
        } catch (error) {
            if (!axios.isCancel(error)) {
                console.error('Error while generating contract comparison summary: ', error);
            }
        }
    }

    async openOrLoad(nodes: NodeBaseDto[]) {
        if (!this.hasPreviousContractNodes) {
            await this.prepareComparison(nodes);
            return;
        }

        this.refreshComparison(nodes);
        this.setIsSelected(true);
    }

    saveChanges() {
        this.store.saveContractComparisonChanges(this);
    }

    reset() {
        this.cancelFetchDataRequest();
        this.cancelSummaryRequest();
        this.summaryRefreshPromise = null;
        this.summaryRefreshPayloadHash = null;
        this.disposeComparisonNodes();
        this.newContractNodes = [];
        this.previousContractNodes = [];
        this.comparisonNodes = [];
        this.fetchPreviousContractStatus = 'idle';
        this.isLoading = false;
        this.isSelected = false;
        this.isSummaryLoading = false;
        this.clearSummaryState();
    }

    dispose() {
        this.summaryRegenerationDisposer();
        this.reset();
    }

    private disposeComparisonNodes() {
        this.comparisonNodes.forEach(comparisonNode => comparisonNode.dispose());
    }

    private createComparisonNodes() {
        this.disposeComparisonNodes();

        runInAction(() => {
            // Some nodes may be added or removed, so we need to get all unique node ids and names to display changes
            this.comparisonNodes = uniqBy([...this.newContractNodes, ...this.previousContractNodes], 'id')
                .map(node => ({
                    nodeId: node.id,
                    nodeName: node.name
                }))
                .map(node =>
                    ComparisonNodeFactory.createComparisonNode({
                        nodeId: node.nodeId,
                        nodeName: node.nodeName,
                        newNode: this.newContractNodes.find(n => n.id === node.nodeId),
                        previousNode: this.previousContractNodes.find(n => n.id === node.nodeId),
                        layerId: this.parameters.layerId
                    })
                )
                .filter(InstanceUtils.isComparisonNode);
            this.refreshSummaryState();
        });
    }

    private initializeNewContractNodes(nodes: NodeBaseDto[]) {
        runInAction(() => {
            this.newContractNodes = NodeFactory.createOrUpdateNodes(nodes, [], this.sourceStore.sources);

            const layerId = this.parameters.layerId;

            if (layerId) {
                this.newContractNodes.forEach(node => {
                    // Only keep the selected layer for comparison
                    if (InstanceUtils.isLayersNode(node)) {
                        node.filterLayers([layerId]);
                    }
                });
            }

            this.newContractInputs.forEach(input => {
                input.setIsControlsHidden(true);

                if (InstanceUtils.isInputWithSource(input)) {
                    input.fetchSourceOptions();
                }
            });
        });
    }

    private initializePreviousContractNodes(nodes: NodeBaseDto[]) {
        runInAction(() => {
            this.previousContractNodes = NodeFactory.createOrUpdateNodes(nodes, [], this.sourceStore.sources);

            this.previousContractInputs.forEach(input => {
                input.setIsReadOnly(true);
                input.setIsControlsHidden(true);

                if (InstanceUtils.isInputWithSource(input)) {
                    input.fetchSourceOptions();
                }
            });
        });
    }

    private cancelFetchDataRequest() {
        if (this.fetchDataCancelSource) {
            this.fetchDataCancelSource.cancel();
            this.fetchDataCancelSource = null;
        }
    }

    private isFetchDataRequestCurrent(cancelSource: CancelTokenSource) {
        return this.fetchDataCancelSource === cancelSource;
    }

    private cancelSummaryRequest() {
        if (this.summaryCancelSource) {
            this.summaryCancelSource.cancel();
            this.summaryCancelSource = null;
        }
    }

    private isSummaryRequestCurrent(cancelSource: CancelTokenSource) {
        return this.summaryCancelSource === cancelSource;
    }

    private get summaryPayloadState(): SummaryPayloadState | null {
        if (!this.comparisonNodes.length) {
            return null;
        }

        const signatureParts: string[] = [];
        const payloadEntries = this.comparisonNodes.flatMap(node =>
            Array.from(node.contractChangeTracker.inputGroupChangeTrackers.values()).map(inputGroupChangeTracker => {
                signatureParts.push(inputGroupChangeTracker.groupName);

                return {
                    entryName: inputGroupChangeTracker.groupName,
                    changes: inputGroupChangeTracker.aggregatedChanges.changeRecords.map(record => {
                        const changeType = record.changeType === 'unsaved' ? 'modified' : record.changeType;
                        const stableNewValue = this.getStableSummaryValue(record.newInput);
                        const stablePreviousValue = this.getStableSummaryValue(record.previousInput);

                        signatureParts.push(
                            record.inputId,
                            record.inputName,
                            changeType,
                            this.serializeSummarySignatureValue(stableNewValue),
                            this.serializeSummarySignatureValue(stablePreviousValue)
                        );

                        return {
                            inputId: record.inputId,
                            inputName: record.inputName,
                            newValue: this.getSummaryValue(record.newInput),
                            previousValue: this.getSummaryValue(record.previousInput),
                            changeType
                        };
                    }),
                    sections: []
                };
            })
        );

        return {
            requestJson: JSON.stringify(payloadEntries),
            signatureHash: Utils.stringToHash(signatureParts.join('\u0001'))
        };
    }

    private get summaryPayloadJson(): string | null {
        return this.summaryPayloadState?.requestJson ?? null;
    }

    private async prepareComparison(nodes: NodeBaseDto[]) {
        this.cancelFetchDataRequest();
        this.cancelSummaryRequest();
        this.summaryRefreshPromise = null;
        this.summaryRefreshPayloadHash = null;

        const cancelSource = axios.CancelToken.source();
        this.fetchDataCancelSource = cancelSource;

        runInAction(() => {
            this.isLoading = true;
            this.fetchPreviousContractStatus = 'idle';
            this.previousContractNodes = [];
            this.clearSummaryState();
        });
        this.initializeNewContractNodes(nodes);

        try {
            const hasLoadedPreviousContract = await this.loadPreviousContractData(cancelSource);

            if (!hasLoadedPreviousContract || !this.isFetchDataRequestCurrent(cancelSource)) {
                return;
            }

            this.createComparisonNodes();

            if (!this.store.isComparisonSummaryConfigured) {
                return;
            }

            await Promise.race([
                this.generateContractComparisonSummary(),
                ContractComparisonModel.wait(summaryPreparationTimeoutMs)
            ]);
        } finally {
            if (this.isFetchDataRequestCurrent(cancelSource)) {
                runInAction(() => {
                    this.isLoading = false;
                });
                this.fetchDataCancelSource = null;
            }
        }
    }

    private refreshComparison(nodes: NodeBaseDto[]) {
        this.initializeNewContractNodes(nodes);
        this.createComparisonNodes();
    }

    private async loadPreviousContractData(cancelSource: CancelTokenSource): Promise<boolean> {
        try {
            const response = await this.store.fetchPreviousContractData(
                { businessId: this.businessId!, layerId: this.parameters.layerId },
                cancelSource.token
            );

            if (!this.isFetchDataRequestCurrent(cancelSource)) {
                return false;
            }

            if (!response.isOk()) {
                runInAction(() => {
                    this.fetchPreviousContractStatus = 'failed';
                });
                return false;
            }

            let hasLoadedPreviousContract = false;

            response.map(result => {
                runInAction(() => {
                    if (result.status === 'BusinessNotFound') {
                        this.fetchPreviousContractStatus = 'notFound';
                        return;
                    }

                    this.initializePreviousContractNodes(result.nodes);
                    this.fetchPreviousContractStatus = 'idle';
                    hasLoadedPreviousContract = true;
                });
            });

            return hasLoadedPreviousContract;
        } catch (error) {
            if (!axios.isCancel(error) && this.isFetchDataRequestCurrent(cancelSource)) {
                console.error(error);
                runInAction(() => {
                    this.fetchPreviousContractStatus = 'failed';
                });
            }

            return false;
        }
    }

    private ensureSummaryRefreshPromise(targetPayloadHash: number): Promise<void> {
        if (this.summaryRefreshPromise && this.summaryRefreshPayloadHash === targetPayloadHash) {
            return this.summaryRefreshPromise;
        }

        const summaryRefreshPromise = this.waitForSummaryInputsAndGenerate(targetPayloadHash).finally(() => {
            if (this.summaryRefreshPromise === summaryRefreshPromise) {
                this.summaryRefreshPromise = null;
                this.summaryRefreshPayloadHash = null;
            }
        });

        this.summaryRefreshPromise = summaryRefreshPromise;
        this.summaryRefreshPayloadHash = targetPayloadHash;

        return summaryRefreshPromise;
    }

    private async waitForSummaryInputsAndGenerate(targetPayloadHash: number) {
        let currentTargetPayloadHash = targetPayloadHash;

        while (currentTargetPayloadHash !== null) {
            while (this.summaryPayloadHash === currentTargetPayloadHash && this.isAnyInputLoading) {
                await ContractComparisonModel.wait(summaryRefreshPollIntervalMs);
            }

            const latestPayloadHash = this.summaryPayloadHash;

            if (latestPayloadHash === null) {
                return;
            }

            if (latestPayloadHash !== currentTargetPayloadHash) {
                currentTargetPayloadHash = latestPayloadHash;
                this.summaryRefreshPayloadHash = latestPayloadHash;
                continue;
            }

            await this.requestCurrentSummary(currentTargetPayloadHash);
            return;
        }
    }

    private async requestCurrentSummary(targetPayloadHash: number) {
        const summaryPayloadState = this.summaryPayloadState;
        const summaryPayloadJson = this.summaryPayloadJson;

        if (
            summaryPayloadState === null ||
            summaryPayloadJson === null ||
            summaryPayloadState.signatureHash !== targetPayloadHash ||
            this.lastCompletedSummaryPayloadHash === targetPayloadHash
        ) {
            return;
        }

        if (this.isSummaryLoading && this.lastRequestedSummaryPayloadHash === targetPayloadHash) {
            return;
        }

        this.cancelSummaryRequest();
        const cancelSource = axios.CancelToken.source();
        this.summaryCancelSource = cancelSource;

        runInAction(() => {
            this.lastRequestedSummaryPayloadHash = targetPayloadHash;
            this.isSummaryLoading = true;
        });

        try {
            const response = await this.store.generateContractComparisonSummary(
                summaryPayloadJson,
                cancelSource.token
            );

            if (!this.isSummaryRequestCurrent(cancelSource) || this.summaryPayloadHash !== targetPayloadHash) {
                runInAction(() => {
                    this.lastRequestedSummaryPayloadHash = null;
                });
                return;
            }

            if (!response.isOk()) {
                runInAction(() => {
                    this.generatedSummary = null;
                    this.lastCompletedSummaryPayloadHash = targetPayloadHash;
                    this.lastGeneratedSummaryPayloadHash = null;
                    this.lastRequestedSummaryPayloadHash = null;
                });
                return;
            }

            response.map(result => {
                runInAction(() => {
                    this.generatedSummary = result.summary;
                    this.lastCompletedSummaryPayloadHash = targetPayloadHash;
                    this.lastGeneratedSummaryPayloadHash = result.summary ? targetPayloadHash : null;
                });
            });
        } catch (error) {
            if (!axios.isCancel(error) && this.isSummaryRequestCurrent(cancelSource)) {
                console.error('Error while generating contract comparison summary: ', error);
                runInAction(() => {
                    this.generatedSummary = null;
                    this.lastCompletedSummaryPayloadHash = targetPayloadHash;
                    this.lastGeneratedSummaryPayloadHash = null;
                    this.lastRequestedSummaryPayloadHash = null;
                });
            }
        } finally {
            if (this.isSummaryRequestCurrent(cancelSource)) {
                runInAction(() => {
                    this.isSummaryLoading = false;
                });
                this.summaryCancelSource = null;
            }
        }
    }

    private refreshSummaryState() {
        if (this.summaryPayloadHash !== this.lastGeneratedSummaryPayloadHash) {
            this.generatedSummary = null;
        }
    }

    private clearSummaryState() {
        this.generatedSummary = null;
        this.lastRequestedSummaryPayloadHash = null;
        this.lastCompletedSummaryPayloadHash = null;
        this.lastGeneratedSummaryPayloadHash = null;
    }

    private getSummaryValue(input: InputBaseModel | undefined): unknown {
        if (input?.dataType === 'DateTime') {
            return input.dto.value ?? null;
        }

        return input?.formattedValue ?? null;
    }

    private getStableSummaryValue(input: InputBaseModel | undefined): unknown {
        return input?.dto.value ?? null;
    }

    private serializeSummarySignatureValue(value: unknown): string {
        return JSON.stringify(value) ?? 'null';
    }
}
